import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BellIcon } from '../../components/BellIcon';
import { MessengerChatShell } from '../../components/messaging/MessengerChatShell';
import { MessengerComposer } from '../../components/messaging/MessengerComposer';
import { MessengerMessageActionSheet } from '../../components/messaging/MessengerMessageActionSheet';
import { MessengerMessageBubble } from '../../components/messaging/MessengerMessageBubble';
import { MissedCallChip } from '../../components/messaging/MissedCallChip';
import { MessengerMessageInfoSheet } from '../../components/messaging/MessengerMessageInfoSheet';
import { MessengerPinnedStrip } from '../../components/messaging/MessengerPinnedStrip';
import { useCall } from '../../contexts/CallContext';
import { getMessengerGroupMeta } from '../../components/messaging/messengerUtils';
import { buildReplyPayload, copyTextToClipboard, filterVisibleMessages, formatSeenTimestamp, getPinnedMessage, resolveOwnStatusVariant, summarizeMessageText } from '../../components/messaging/messengerThreadUtils';
import { formatChatDisplayName } from '../../utils/nameFormatters';
import { buildMessageHeaders } from '../../utils/messageHeaders';
import { fetchAccountSettings } from '../../utils/accountSettingsApi';
import { primeCallPermissions } from '../../utils/callPermissions';
import { SupportClosedBanner, renderSupportSystemMessage } from '../../components/messaging/SupportSystemCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HostChatPage() {
  const { convoId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [actionTarget, setActionTarget] = useState(null);
  const [infoTarget, setInfoTarget] = useState(null);
  const [myAllowCalls, setMyAllowCalls] = useState(true);
  const { startVoiceCall } = useCall();

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    // Prime mic permission the FIRST time the host opens ANY chat, not
    // just the inbox — deep-links (push tap, share) can drop a host
    // straight into a conversation, and tapping the phone button without
    // an OS mic grant silently fails with "Unable to start the call".
    primeCallPermissions('host');
    fetchAccountSettings()
      .then((settings) => setMyAllowCalls(settings.privacy?.allow_phone_calls !== false))
      .catch(() => {});
  }, []);

  const logHostChatError = useCallback((message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(message, error);
    }
  }, []);

  const fetchConversation = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/conversations/${convoId}`, {
        headers: buildMessageHeaders(token, null, 'host'),
      });
      setConversation(response.data.conversation || null);
      setMessages(response.data.messages || []);
      setOtherUser(response.data.other_user || null);
    } catch (error) {
      logHostChatError('Failed to load host chat', error);
      toast.error(error.response?.data?.detail || 'Failed to load this conversation.');
    } finally {
      setLoading(false);
    }
  }, [convoId, logHostChatError]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  // Live-refresh on incoming Socket.IO `conversation_message_received` event
  // so the host sees guest messages appear in real time without reloading.
  useEffect(() => {
    const handler = (event) => {
      if (event?.detail?.conversation_id && event.detail.conversation_id !== convoId) return;
      fetchConversation();
    };
    window.addEventListener('allure:conversation-message-received', handler);
    return () => window.removeEventListener('allure:conversation-message-received', handler);
  }, [convoId, fetchConversation]);

  // WhatsApp-style tick flip: patch delivered/read state in place when
  // the guest acks the message on their end. Avoids a full refetch.
  useEffect(() => {
    const applyStatus = (kind) => (event) => {
      const detail = event?.detail || {};
      if (detail.conversation_id && detail.conversation_id !== convoId) return;
      const ids = new Set(detail.message_ids || []);
      if (!ids.size) return;
      setMessages((prev) => prev.map((message) => {
        if (!ids.has(message.id)) return message;
        if (kind === 'delivered') return { ...message, delivered: true, delivered_at: detail.at || message.delivered_at };
        return { ...message, read: true, read_at: detail.at || message.read_at, delivered: true, delivered_at: message.delivered_at || detail.at };
      }));
    };
    const delivered = applyStatus('delivered');
    const read = applyStatus('read');
    window.addEventListener('allure:conversation-messages-delivered', delivered);
    window.addEventListener('allure:conversation-messages-read', read);
    return () => {
      window.removeEventListener('allure:conversation-messages-delivered', delivered);
      window.removeEventListener('allure:conversation-messages-read', read);
    };
  }, [convoId]);

  const visibleMessages = useMemo(() => filterVisibleMessages(messages), [messages]);

  const pinnedMessage = useMemo(() => getPinnedMessage(visibleMessages), [visibleMessages]);
  const latestOutgoingMessageId = useMemo(() => [...visibleMessages].reverse().find((message) => (conversation?.participants?.[0] === message.sender_id || message.sender_id === user.id))?.id || null, [conversation?.participants, user.id, visibleMessages]);
  const buildStatusText = useCallback((message) => {
    if (!message?.read) return '';
    const seenTime = formatSeenTimestamp(message?.read_at);
    return seenTime ? `Seen · ${seenTime}` : '';
  }, []);

  const sendMessage = useCallback(async ({ content = '', messageType = 'text', metadata = {}, previewText = '' }) => {
    try {
      const token = localStorage.getItem('token');
      const enrichedMetadata = replyingTo ? { ...metadata, reply_to: replyingTo } : metadata;
      const response = await axios.post(`${API}/messages/send`, {
        conversation_id: convoId,
        content,
        message_type: messageType,
        metadata: enrichedMetadata,
        preview_text: previewText,
      }, {
        headers: buildMessageHeaders(token, null, 'host'),
      });
      setMessages((prev) => [...prev, response.data]);
      setReplyingTo(null);
    } catch (error) {
      logHostChatError('Failed to send host message', error);
      toast.error(error.response?.data?.detail || 'Failed to send the message.');
      throw error;
    }
  }, [convoId, logHostChatError, replyingTo]);

  const handleSendText = useCallback(async (text) => {
    await sendMessage({ content: text, messageType: 'text', previewText: text });
    setNewMessage('');
  }, [sendMessage]);

  const handleSendImage = useCallback(async (metadata) => {
    await sendMessage({ content: '', messageType: 'image', metadata, previewText: metadata.source === 'camera' ? 'Sent a photo' : 'Sent an image' });
  }, [sendMessage]);

  const handleSendVideo = useCallback(async (metadata) => {
    await sendMessage({ content: '', messageType: 'video', metadata, previewText: 'Sent a video' });
  }, [sendMessage]);

  const handleSendAudio = useCallback(async (metadata) => {
    await sendMessage({ content: '', messageType: 'audio', metadata, previewText: 'Sent a voice message' });
  }, [sendMessage]);

  const isSupportConvo = conversation?.type === 'support';
  const supportClosed = isSupportConvo && conversation?.support_status === 'closed';
  // Support threads always surface as "Allure" + Allure logo so a reply
  // from any admin reads as one consistent "Allure" identity (not the
  // individual admin's name/photo).
  const SUPPORT_CONTACT_NAME = 'Allure';
  const SUPPORT_CONTACT_IMAGE = '/notification-icon-192.png';
  const guestName = isSupportConvo ? SUPPORT_CONTACT_NAME : (formatChatDisplayName(otherUser) || 'Guest');
  const contactImage = isSupportConvo ? SUPPORT_CONTACT_IMAGE : (otherUser?.image_url || otherUser?.photo_url);
  // Hide the phone button when EITHER side has "Allow phone calls" off, or
  // when the other side isn't a guest (host can only ring guests).
  // Server-side verdict: conversation-scoped guest↔host pair + both privacy
  // toggles ON (support threads and same-role chats never get the button).
  const showCallButton = useMemo(() => {
    if (!conversation?.can_call) return false;
    if (otherUser?.allow_phone_calls === false) return false;
    return myAllowCalls;
  }, [conversation?.can_call, otherUser?.allow_phone_calls, myAllowCalls]);
  // Red dot on the phone icon when there's an unread missed-call event
  // for the host in this thread (guest tried to call, host didn't pick
  // up). Filters to `no_answer|missed` — `declined` chips don't warrant
  // a badge because the caller already knows they were declined.
  const hasMissedCall = useMemo(() => visibleMessages.some((m) => (
    m.message_type === 'call_event'
    && m.recipient_id === user.id
    && !m.read
    && ['no_answer', 'missed'].includes((m.metadata || {}).status)
  )), [visibleMessages, user.id]);
  const composer = supportClosed ? (
    <SupportClosedBanner
      onOpenNewTicket={() => navigate('/support/contact')}
      dataTestId="host-chat-support-closed-banner"
    />
  ) : (
    <MessengerComposer
      value={newMessage}
      onChange={(event) => setNewMessage(event.target.value)}
      onSendText={handleSendText}
      onSendImage={handleSendImage}
      onSendVideo={handleSendVideo}
      onSendAudio={handleSendAudio}
      replyingTo={replyingTo}
      onCancelReply={() => setReplyingTo(null)}
      editingMessage={editingMessage}
      onCancelEdit={() => { setEditingMessage(null); setNewMessage(''); }}
      onSaveEdit={async (message, text) => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${API}/messages/${message.id}/edit`, { content: text }, { headers: { Authorization: `Bearer ${token}` } });
          setMessages((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
          setEditingMessage(null);
          setNewMessage('');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to edit the message.');
        }
      }}
      placeholder="Message"
      dataTestIdPrefix="host-chat"
    />
  );

  const applyUpdatedMessage = useCallback((updatedMessage) => {
    setMessages((prev) => prev.map((message) => (message.id === updatedMessage.id ? updatedMessage : message)));
  }, []);

  const reactToMessage = useCallback(async (message, emoji, silent = false) => {
    try {
      const token = localStorage.getItem('token');
      const currentReaction = message?.metadata?.reactions?.[user.id] || '';
      const nextEmoji = currentReaction === emoji ? '' : emoji;
      const response = await axios.post(`${API}/messages/${message.id}/reaction`, { emoji: nextEmoji }, { headers: { Authorization: `Bearer ${token}` } });
      applyUpdatedMessage(response.data);
      setActionTarget(null);
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.detail || 'Failed to react to the message.');
    }
  }, [applyUpdatedMessage, user.id]);

  const copyMessage = useCallback(async (message) => {
    const copied = await copyTextToClipboard(summarizeMessageText(message));
    if (copied) toast.success('Copied');
    else toast.error('Unable to copy this message.');
    setActionTarget(null);
  }, []);

  const pinMessage = useCallback(async (message, pinned = true) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/messages/${message.id}/pin`, { pinned }, { headers: { Authorization: `Bearer ${token}` } });
      setMessages((prev) => prev.map((item) => {
        if (item.conversation_id !== message.conversation_id) return item;
        if (item.id === response.data.id) return response.data;
        return { ...item, metadata: { ...(item.metadata || {}), pinned: false } };
      }));
      setActionTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pin the message.');
    }
  }, []);

  const unsendMessage = useCallback(async (message) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages/${message.id}/unsend`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMessages((prev) => prev.filter((item) => item.id !== message.id));
      if (replyingTo?.id === message.id) setReplyingTo(null);
      setActionTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unsend the message.');
    }
  }, [replyingTo?.id]);

  return (
    <>
      <MessengerChatShell
      pageTestId="host-chat-page"
      bellNode={<button data-testid="host-chat-bell-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><BellIcon /></button>}
      onBack={() => navigate('/host/messages')}
      backTestId="host-chat-back-button"
      contactName={guestName}
      contactImage={contactImage}
      contactTestId="host-chat-contact-avatar"
      showCallButton={showCallButton}
      onCall={() => startVoiceCall({ conversationId: convoId, contact: otherUser })}
      callTestId="host-chat-phone-button"
      callBadge={hasMissedCall && showCallButton}
      messages={visibleMessages}
      loading={loading}
      emptyText="No messages yet"
      composer={composer}
      pinnedNode={pinnedMessage ? <MessengerPinnedStrip message={pinnedMessage} previewText={summarizeMessageText(pinnedMessage)} onClear={() => pinMessage(pinnedMessage, false)} /> : null}
      scrollDependency={visibleMessages.length}
      renderMessage={(message, index) => {
        const isRequestCard = message.message_type === 'invite_request' && (message.metadata?.status || 'pending_host_approval') === 'pending_host_approval';
        const isStatusCard = message.message_type === 'invite_status';
        const isMine = conversation?.participants?.[0] === message.sender_id || message.sender_id === user.id;
        const metadata = message.metadata || {};
        const messageKey = `${message.id}-${message.created_at || message.sender_id || 'message'}`;
        const supportRender = renderSupportSystemMessage(message, {
          conversation,
          isMine,
          onResolved: () => fetchConversation(),
          dataTestId: `host-chat-support-${messageKey}`,
        });
        if (supportRender) return supportRender.node;
        if (message.message_type === 'call_event') {
          return (
            <MissedCallChip
              key={messageKey}
              message={message}
              viewerId={user.id}
              canCallBack={showCallButton}
              onCallBack={() => startVoiceCall({ conversationId: convoId, contact: otherUser })}
              dataTestId={`host-chat-missed-call-${messageKey}`}
            />
          );
        }
        const groupMeta = getMessengerGroupMeta(visibleMessages, index, (candidate) => ((conversation?.participants?.[0] === candidate.sender_id || candidate.sender_id === user.id) ? 'me' : 'other'));

        return (
          <MessengerMessageBubble
            key={messageKey}
            message={message}
            isMine={isMine}
            avatarName={isMine ? (user.name || 'You') : guestName}
            avatarImage={isMine ? (user.photo_url || user.image_url) : contactImage}
            dataTestId={`host-chat-message-${messageKey}`}
            showAvatar={!groupMeta.nextSameSender}
            groupPosition={groupMeta.groupPosition}
            spacingMode="host"
            statusText={isMine && message.id === latestOutgoingMessageId ? buildStatusText(message) : ''}
            statusVariant={isMine ? resolveOwnStatusVariant(message) : null}
            onReply={(targetMessage, mine) => setReplyingTo(buildReplyPayload(targetMessage, mine ? (user.name || 'You') : guestName))}
            onLongPress={(targetMessage, mine) => setActionTarget({ message: targetMessage, isMine: mine })}
            onDoubleTapHeart={(targetMessage) => reactToMessage(targetMessage, '❤️', true)}
          >
            {!isMine && isRequestCard ? (
              <div data-testid={`host-invite-request-card-${metadata.invite_id}`} style={{ background: 'var(--status-info-surface)', borderRadius: 20, padding: '14px 18px', border: '1px solid var(--accent-border-soft)' }}>
                <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--status-info-text)', margin: 0 }}>Pending approval for {metadata.listing_name}</p>
                <button onClick={() => navigate(`/host/invites/${metadata.invite_id}/pending`)} data-testid={`host-view-request-${metadata.invite_id}`} style={{ marginTop: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--status-info-text)' }}>View request →</button>
              </div>
            ) : null}
            {!isMine && isStatusCard ? (
              <div data-testid={`host-invite-status-${metadata.invite_id || message.id}`} style={{ background: metadata.status === 'approved' ? 'var(--status-success-surface)' : 'var(--status-danger-surface)', borderRadius: 18, padding: '10px 16px', border: '1px solid var(--accent-border-soft)' }}>
                <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: metadata.status === 'approved' ? 'var(--status-success-text)' : 'var(--status-danger-text)', margin: 0, fontWeight: 700 }}>{metadata.status === 'approved' ? 'Request confirmed' : 'Request declined'}</p>
              </div>
            ) : null}
          </MessengerMessageBubble>
        );
      }}
    />
      <MessengerMessageActionSheet
        open={Boolean(actionTarget)}
        message={actionTarget?.message}
        isMine={Boolean(actionTarget?.isMine)}
        onClose={() => setActionTarget(null)}
        onReact={(emoji) => reactToMessage(actionTarget.message, emoji)}
        onCopy={(message) => copyMessage(message)}
        onInfo={(message) => { setInfoTarget({ message, isMine: Boolean(actionTarget?.isMine) }); setActionTarget(null); }}
        onPin={(message) => pinMessage(message)}
        onReply={(message) => { setReplyingTo(buildReplyPayload(message, actionTarget?.isMine ? (user.name || 'You') : guestName)); setActionTarget(null); }}
        onEdit={(message) => { setEditingMessage(message); setNewMessage(message.content || ''); setReplyingTo(null); setActionTarget(null); }}
        onUnsend={(message) => unsendMessage(message)}
      />
      <MessengerMessageInfoSheet
        open={Boolean(infoTarget)}
        message={infoTarget?.message}
        isMine={Boolean(infoTarget?.isMine)}
        currentUser={{ id: user.id, name: user.name || 'You', image_url: user.photo_url || user.image_url }}
        otherUser={otherUser}
        onClose={() => setInfoTarget(null)}
        dataTestId="host-message-info-sheet"
      />
    </>
  );
}